import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { ServicesGateway } from '@wilson/api/gateway';
import {
  Label,
  OrganizationalUnit,
  Service,
  Shift,
  ShiftCategory,
  ShiftSeriesShift,
  TimelineDateQueryParamKey,
  TimelineShowActivitiesQueryParamKey,
  TimelineUnassignedServiceIdsQueryParamKey,
  TimelineUnassignedServiceSearchesQueryParamKey,
  TimelineUnassignedShiftIdsQueryParamKey,
  TimelineUnassignedShiftSearchesQueryParamKey,
  User,
  WithPreparedAttributes,
  ZOOM_LEVEL,
  ZoomLevelQueryParamKey,
} from '@wilson/interfaces';
import { SELECTED_REGION, TabEnum } from '@wilson/shift-timeline/services';
import {
  AddActivityToUnassignedRegion,
  AddShiftWithoutActivitiesToUnassignedRegion,
  HideLoaderWhenLoadingUnassignedShiftsOrActivities,
  HighlightSelectedServices,
  HighlightSelectedShifts,
  PinUsersOnTimeline,
  ShiftTimelineDataTransformerService,
  TimelineUpsertNewShiftV2,
  ToggleExpandUnassignedRegion,
} from '@wilson/shift-timeline/state';
import { ShiftsService } from '@wilson/shifts';
import { isBefore } from 'date-fns';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { firstValueFrom, forkJoin, map } from 'rxjs';

@Component({
  selector: 'wilson-shift-timeline-redirect',
  templateUrl: './shift-timeline-redirect.component.html',
  styleUrls: ['./shift-timeline-redirect.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NzSkeletonModule],
})
export class ShiftTimelineRedirectComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly shiftsService = inject(ShiftsService);
  private readonly servicesGateway = inject(ServicesGateway);
  private readonly location = inject(Location);
  private readonly nzMessageService = inject(NzMessageService);
  private readonly translateService = inject(TranslateService);
  private readonly store = inject(Store);
  private readonly shiftTimelineDataTransformerService = inject(
    ShiftTimelineDataTransformerService,
  );

  async ngOnInit(): Promise<void> {
    const unassignedShiftIdsQueryParam = this.activatedRoute.snapshot
      .queryParams[TimelineUnassignedShiftIdsQueryParamKey] as string;
    const unassignedShiftSearchesQueryParam = this.activatedRoute.snapshot
      .queryParams[TimelineUnassignedShiftSearchesQueryParamKey] as string;
    const unassignedServiceIdsQueryParam = this.activatedRoute.snapshot
      .queryParams[TimelineUnassignedServiceIdsQueryParamKey] as string;
    const unassignedServiceSearchesQueryParam = this.activatedRoute.snapshot
      .queryParams[TimelineUnassignedServiceSearchesQueryParamKey] as string;

    if (unassignedShiftIdsQueryParam) {
      await this.redirectToUnassignShifts(
        unassignedShiftIdsQueryParam,
        unassignedShiftSearchesQueryParam,
      );
    } else if (unassignedServiceIdsQueryParam) {
      await this.redirectToUnassignServices(
        unassignedServiceIdsQueryParam,
        unassignedServiceSearchesQueryParam,
      );
    } else {
      this.router.navigate(['shift-timeline']);
    }
  }

  private async redirectToUnassignShifts(
    unassignedShiftIdsQueryParam: string,
    unassignedShiftSearchesQueryParam: string,
  ): Promise<void> {
    try {
      const unassignedShiftIds = unassignedShiftIdsQueryParam.split(',');
      const shiftObservables = unassignedShiftIds.map((id) =>
        this.shiftsService.get<
          Shift &
            WithPreparedAttributes & {
              id: string;
              organizationalUnit: OrganizationalUnit | null;
              user: User | null;
              shiftCategory: ShiftCategory | null;
              shiftSeries: ShiftSeriesShift | null;
              labels: Label[];
            }
        >(id, {
          relations: [
            'organizationalUnit',
            'user',
            'shiftCategory',
            'shiftSeries',
            'labels',
            'startLocation',
            'endLocation',
          ],
        }),
      );
      const shifts = await firstValueFrom(forkJoin(shiftObservables));
      shifts.sort((shiftA, shiftB) => {
        if (isBefore(new Date(shiftB.startDate), new Date(shiftA.startDate)))
          return 1;
        else return -1;
      });

      this.store.dispatch(new ToggleExpandUnassignedRegion(true));
      this.setShiftToStateAndRedirectToTimeline(
        shifts,
        unassignedShiftSearchesQueryParam,
      );
    } catch {
      this.nzMessageService.error(
        this.translateService.instant('general.something_went_wrong'),
      );
      this.location.back();
    }
  }

  private async redirectToUnassignServices(
    unassignedServiceIdsQueryParam: string,
    unassignedServiceSearchesQueryParam: string,
  ): Promise<void> {
    try {
      const unassignedServiceIds = unassignedServiceIdsQueryParam.split(',');
      const serviceObservables = unassignedServiceIds.map((id) =>
        this.servicesGateway
          .getResolvedService(id)
          .pipe(map((services) => services[0])),
      );
      const services = await firstValueFrom(forkJoin(serviceObservables));
      services.sort((serviceA, serviceB) => {
        if (
          isBefore(new Date(serviceB.startDate), new Date(serviceA.startDate))
        )
          return 1;
        else return -1;
      });

      this.store.dispatch(new ToggleExpandUnassignedRegion(true));
      this.setServiceToStateAndRedirectToTimeline(
        services,
        unassignedServiceSearchesQueryParam,
      );
    } catch {
      this.nzMessageService.error(
        this.translateService.instant('general.something_went_wrong'),
      );
      this.location.back();
    }
  }

  private setShiftToStateAndRedirectToTimeline(
    shifts: (Shift &
      WithPreparedAttributes & {
        id: string;
      })[],
    unassignedShiftSearchesQueryParam: string,
  ): void {
    const unassignedShiftIds: string[] = [];
    const unassignedShiftUserIds: string[] = [];

    shifts.forEach((shift) => {
      if (shift.userId) {
        unassignedShiftUserIds.push(shift.userId);
        this.store.dispatch(
          new TimelineUpsertNewShiftV2({
            shift: {
              ...shift,
              isHighlighted: true,
            },
          }),
        );
      } else {
        const convertedShift =
          this.shiftTimelineDataTransformerService.convertToUnassignedShift(
            shift,
          );
        unassignedShiftIds.push(shift.id);
        this.store.dispatch([
          new HideLoaderWhenLoadingUnassignedShiftsOrActivities(true),
          new AddShiftWithoutActivitiesToUnassignedRegion(convertedShift),
        ]);
      }
    });

    this.store.dispatch([
      new HighlightSelectedShifts(shifts.map((shift) => shift.id)),
      new PinUsersOnTimeline(unassignedShiftUserIds),
    ]);

    this.router.navigate(['shift-timeline'], {
      queryParams: {
        [TimelineDateQueryParamKey]: shifts[0].startDate,
        [ZoomLevelQueryParamKey]: ZOOM_LEVEL.DEFAULT,
        [SELECTED_REGION]: TabEnum.UnassignedShifts,
        [TimelineUnassignedShiftIdsQueryParamKey]:
          unassignedShiftIds.join(',') || null,
        [TimelineUnassignedShiftSearchesQueryParamKey]:
          unassignedShiftSearchesQueryParam || null,
      },
    });
  }

  private setServiceToStateAndRedirectToTimeline(
    services: (Service & { id: string })[],
    unassignedServiceSearchesQueryParam: string,
  ): void {
    const unassignedServiceIds: string[] = [];

    services.forEach((service) => {
      unassignedServiceIds.push(service.id);
      service.activities?.forEach((activity) => {
        if (!activity.shiftId) {
          this.store.dispatch([new AddActivityToUnassignedRegion(activity)]);
        }
      });
    });

    if (unassignedServiceIds.length) {
      this.store.dispatch([
        new HideLoaderWhenLoadingUnassignedShiftsOrActivities(true),
      ]);
    }
    this.store.dispatch(
      new HighlightSelectedServices(services.map((service) => service.id)),
    );

    this.router.navigate(['shift-timeline'], {
      queryParams: {
        [TimelineDateQueryParamKey]: services[0].startDate,
        [ZoomLevelQueryParamKey]: ZOOM_LEVEL.DEFAULT,
        [SELECTED_REGION]: TabEnum.UnassignedActivities,
        [TimelineShowActivitiesQueryParamKey]: true,
        [TimelineUnassignedServiceIdsQueryParamKey]:
          unassignedServiceIds.join(',') || null,
        [TimelineUnassignedServiceSearchesQueryParamKey]:
          unassignedServiceSearchesQueryParam || null,
      },
    });
  }
}
